/* eslint-disable jsx-a11y/anchor-is-valid */
import { Modal, TextField, Typography } from "@material-ui/core";
import React, { useCallback, useEffect, useState } from "react";

import { Close, RemoveOutlined, AddOutlined } from "@material-ui/icons";

import { useStore } from "../../../store";
import { Item, Product } from "../../../types";

import { formatCurrency } from "../../../utils/format";
import { Button } from "../../index";
import useStyles from "./styles";
import { useEntity } from "../../../hooks/useEntity";
import { getTerminalId } from "../../../services";
import { canAddItemToCart } from "../../../utils/canAddItemToCart";
import { allergens } from "./constants";

export interface ProductModalProps {
  open: boolean;
  onClose: () => void;
  product: Product | Item;
  isEditing?: boolean;
}

export const ProductModal: React.FC<ProductModalProps> = ({
  open,
  onClose,
  product,
  isEditing,
}) => {
  const Styles = useStyles({ picture: product.picture });

  const { order, userTotem } = useStore();

  const { showObservations, allowPurchaseWithNegativeBalance, allowStudentCardMachinePurchase } = useEntity();
  const canBypassBalanceBlock =
    allowStudentCardMachinePurchase &&
    !!getTerminalId() &&
    userTotem.wallet.period === "";
  const canUseNegativeBalance =
    allowPurchaseWithNegativeBalance && userTotem.wallet.period === "";

  const [amount, setAmount] = useState<number>(1);
  const [value, setValue] = useState<string>("");
  const total = amount * (product.price / 100);

  const isAvailable = (): boolean => {
    if (canUseNegativeBalance || canBypassBalanceBlock) {
      return true;
    }
    return (
      (amount + 1) * (product.price / 100) <=
        userTotem.wallet.periodLimit - userTotem.wallet.spent || !userTotem.id
    );
  };

  const available = isAvailable();

  const changeAmount = (number: number) => {
    if (number >= 1 && number <= product.quantity) {
      setAmount(number);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    if (inputValue.length <= 140) {
      setValue(inputValue);
    }
  };

  const [isLoading, setIsLoading] = useState(false);

  const addItemToCart = useCallback(async () => {
    if (product.isLocked) {
      onClose();
      return;
    }

    setIsLoading(true);
    try {
      order.addItem(
        {
          ...product,
          amount,
          observation: value,
        },
        !userTotem.id
      );
      onClose();
    } finally {
      setIsLoading(false);
    }
  }, [amount, value, product, order, userTotem.id, onClose]);

  const editQuantityItemToCart = useCallback(() => {
    order.editQuantity(product.id, amount, value);

    onClose();
  }, [amount, value]);

  const thereIsChange = useCallback((): boolean => {
    if (product?.observation && value && product.amount === amount) {
      return product.observation === value;
    }

    if (amount) {
      return product.amount === amount;
    }

    return false;
  }, [value, amount]);

  useEffect(() => {
    const existingItem = order.items.find(
      (item) => item.id === product.id && item.amount > 0
    );
    if (existingItem) {
      setValue(existingItem.observation || "");
    }
  }, [order.items, product]);

  const disableAddToCart = useCallback((): boolean => {
    if (canUseNegativeBalance || canBypassBalanceBlock) {
      return false;
    }
    if (userTotem.virtualCard) {
      if (isEditing) {
        const newTotal = total - (product.amount / 100) * amount;
        return (
          userTotem.wallet && newTotal > userTotem.virtualCard.balance / 100
        );
      }

      return userTotem.wallet && !canAddItemToCart({
        cartTotalInCents: order.totalAmount,
        itemTotalInCents: total * 100,
        period: userTotem.wallet.period,
        periodLimit: userTotem.wallet.periodLimit,
        spent: userTotem.wallet.spent,
        allowPurchaseWithNegativeBalance,
      });
    }

    return false;
  }, [
    total,
    amount,
    userTotem,
    order,
    isEditing,
    allowPurchaseWithNegativeBalance,
    canUseNegativeBalance,
    canBypassBalanceBlock,
  ]);

  useEffect(() => {
    if (product.amount) {
      setAmount(product.amount);
    }
  }, []);

  const [expanded, setExpanded] = useState(false);
  const [moreInfo, setMoreInfo] = useState(false);

  const text = product.description;
  const maxLength = 60;
  const isLongText = text.length > maxLength;
  const displayedText =
    expanded || !isLongText ? text : `${text.slice(0, maxLength)}...`;

  const openOrClose = () => {
    setExpanded((prev) => !prev);
  };
  const openOrCloseMoreInfo = () => {
    setMoreInfo((prev) => !prev);
  };

  const { allergenics } = product;
  const haveAllergens =
    allergenics &&
    Object.keys(allergenics).some(
      (key) =>
        typeof allergenics[key as keyof typeof allergenics] === "boolean" &&
        allergenics[key as keyof typeof allergenics] === true
    );

  return (
    <Modal className={Styles.modal} open={open} onClose={onClose}>
      <div className={Styles.modalView}>
        <div className={Styles.close} onClick={onClose}>
          <Close />
        </div>

        <div className={Styles.contentContainer}>
          <div className={Styles.info}>
            <div className={Styles.image} />
            <Typography component="span">{product.name}</Typography>
          </div>

          {showObservations && (
            <div className={Styles.observation}>
              <TextField
                id="standard-basic"
                label="Digite uma observação"
                variant="standard"
                inputProps={{ maxLength: 140 }}
                value={value}
                onChange={handleChange}
              />
            </div>
          )}

          <div className={Styles.orderView}>
            <div className={Styles.amount}>
              <div
                className={Styles.icon}
                onClick={() => changeAmount(amount - 1)}
              >
                <RemoveOutlined />
              </div>

              <Typography component="span">{amount}</Typography>

              <div
                className={`${Styles.icon}${
                  !available ? ` ${Styles.iconDisabled}` : ""
                }`}
                onClick={() => available && changeAmount(amount + 1)}
              >
                <AddOutlined />
              </div>
            </div>

            <div className={Styles.total}>
              <Typography component="span" align="left">
                Total
              </Typography>
              <Typography component="span" align="right">
                {formatCurrency(total)}
              </Typography>
            </div>
          </div>

          <Typography component="span" className={Styles.title}>
            Descrição do produto
          </Typography>
          <div className={Styles.descContainer} onClick={openOrClose}>
            <Typography component="span">{`${displayedText} `}</Typography>
            {isLongText && (
              <strong>{expanded ? "Ler menos" : "Ler mais"}</strong>
            )}
          </div>

          <Typography component="span" className={Styles.title}>
            Informações sobre alergênicos
          </Typography>
          <div
            className={Styles.allergens}
            onClick={haveAllergens ? openOrCloseMoreInfo : undefined}
          >
            <div className={Styles.row}>
              {haveAllergens ? (
                allergens
                  .filter(
                    (item) =>
                      product.allergenics &&
                      product.allergenics[
                        item.key as keyof typeof product.allergenics
                      ]
                  )
                  .map((item) => (
                    <div key={item.key} className={Styles.allergensContainer}>
                      <img
                        src={item.icon}
                        alt={item.label}
                        className={Styles.icon}
                      />
                      <Typography component="span">{item.label}</Typography>
                    </div>
                  ))
              ) : (
                <Typography component="span" className={Styles.moreInfo}>
                  Sem informações sobre alergênicos
                </Typography>
              )}
            </div>

            {haveAllergens && (
              <Typography component="span" className={Styles.moreInfo}>
                Toque para
                {" "}
                {moreInfo
                  ? "menos informações sobre alergênicos"
                  : "mais informações sobre alergênicos"}
              </Typography>
            )}
          </div>

          {moreInfo && (
            <div className={Styles.allergens} onClick={openOrCloseMoreInfo}>
              {product.allergenics ? (
                <Typography component="span" className={Styles.moreInfoText}>
                  Alergênicos:
                  {" "}
                  {allergens
                    .filter(
                      (item) =>
                        product.allergenics &&
                        product.allergenics[
                          item.key as keyof typeof product.allergenics
                        ]
                    )
                    .map((item) => item.desc)
                    .join(", ")}
                  .
                </Typography>
              ) : (
                <Typography component="span" className={Styles.moreInfoText}>
                  Sem informações sobre alergênicos
                </Typography>
              )}
            </div>
          )}
        </div>

        <div className={Styles.buttonContainer}>
          <Button
            className={Styles.addToCart}
            onClick={() =>
              isEditing ? editQuantityItemToCart() : addItemToCart()}
            disabled={thereIsChange() || disableAddToCart() || isLoading}
          >
            {isEditing && "Salvar Alterações"}
            {!isEditing && isLoading && "Adicionando..."}
            {!isEditing && !isLoading && "Adicionar ao Carrinho"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
